import { hasValue, toStr } from "@utils";
import { FormatOptions } from "./PillInput";
import { JSONPath } from 'jsonpath-plus';
export function extractValueAndFormatters(expression) {
    const parts = expression.split('|').map(part => part.trim());
    const content = parts[0];
    const formatters = parts.slice(1);
    return { content, formatters };
}
const ExpressionRegexp = /{\$\.[^}]+}|{[^}]+\|[^}]*}/g;
/**
* @export
* Checks if a string contains placeholder expressions that can be parsed
*
* @example
* String with placeholders
* ```tsx
* hasPlaceholders("Hello {user.name}!") // Returns: true
* ```
*
* @example
* Plain string without placeholders
* ```tsx
* hasPlaceholders("Hello world!") // Returns: false
* ```
*/
export function hasPlaceholders(value) {
    const localRegexp = new RegExp(ExpressionRegexp);
    localRegexp.lastIndex = 0;
    return localRegexp.test(value);
}
function isPathValid(data, path) {
    try {
        const paths = JSONPath({ path, json: data, resultType: 'path' });
        return paths.length > 0;
    }
    catch (e) {
        return false;
    }
}
/**
* @export
* Applies formatting transformations to a value based on formatter configuration
* Uses FormatOptions to transform values (string, number, boolean, etc.)
*
* @example
* Format as number with decimals
* ```tsx
* applyFormatters(19.999, ["number", "2"]) // Returns: 19.99
* ```
*
* @example
* No formatters applied
* ```tsx
* applyFormatters("hello", []) // Returns: "hello"
* ```
*/
export function applyFormatters(value, formatters) {
    const formatterId = (!!formatters && hasValue(formatters === null || formatters === void 0 ? void 0 : formatters[0])) ? formatters[0] : null;
    if (!formatterId)
        return value;
    const formatOption = FormatOptions.find(f => f.id === formatterId);
    if (!formatOption)
        return value;
    return formatOption.formatValue(value, formatters.slice(1));
}
/**
* @export
* Extracts and formats a value from data using an expression with optional formatters
* Supports JSONPath expressions starting with $ and applies formatting options
*
* @example
* Extract nested property
* ```tsx
* extractAndFormatValue("$.user.name", { user: { name: "John" } }, "") // Returns: "John"
* ```
*
* @example
* Apply number formatting
* ```tsx
* extractAndFormatValue("$.price|number|2", { price: 19.999 }, "0") // Returns: 19.99
* ```
*/
export function extractAndFormatValue(expression, data, defaultValue) {
    const { content, formatters } = extractValueAndFormatters(expression);
    let rawValue = content;
    if (content === null || content === void 0 ? void 0 : content.startsWith('$')) {
        if (!isPathValid(data, content))
            return defaultValue;
        rawValue = JSONPath({ path: content, json: data, wrap: false });
    }
    return applyFormatters(rawValue, formatters);
}
/**
* @export
* Parses placeholder expressions in a string value and replaces them with actual data
* Returns the original type if single expression, otherwise returns formatted string
*
* @example
* Single expression returns typed value
* ```tsx
* parsePlaceholders("{user.age}", { user: { age: 25 } }) // Returns: 25 (number)
* ```
*
* @example
* Mixed content returns formatted string
* ```tsx
* parsePlaceholders("Hello {user.name}!", { user: { name: "John" } }) // Returns: "Hello John!"
* ```
*/
export function parsePlaceholders(value, data) {
    const strValue = String(value).trim();
    if (!hasValue(strValue))
        return value;
    if (!hasPlaceholders(strValue))
        return value;
    const localRegexp = new RegExp(ExpressionRegexp);
    localRegexp.lastIndex = 0;
    const matches = strValue.match(localRegexp);
    if (!matches || matches.length === 0)
        return value;
    // Single expression that spans the entire value
    if (matches.length === 1 && strValue === matches[0]) {
        const match = matches[0];
        const expression = extractExpressionFromMatch(match);
        return extractAndFormatValue(expression, data, '');
    }
    // Multiple expressions or mixed content - return string
    return strValue.replace(localRegexp, (match) => {
        const expression = extractExpressionFromMatch(match);
        const parsedValue = extractAndFormatValue(expression, data, '');
        return toStr(parsedValue);
    });
}
function extractExpressionFromMatch(match) {
    // remove {} from placeholders
    return match.slice(1, -1).trim();
}
